import { useMemo } from 'react';

import { isEmpty } from 'lodash';
import { useInfiniteQuery } from 'react-query';

import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';

import { LabelItem, LabelList, LabelsResponseType } from '../types';

type Params = {
  enabled?: boolean;
  searchTerm?: string;
};

export const useGetLabels = (params: Params = {}) => {
  const { enabled = true, searchTerm = '' } = params;

  const client = createHttpClient();

  const fetchLabels = ({ pageParam = 'firstPage', limit = 1000 }) => {
    const pagination =
      pageParam === 'firstPage' ? { limit } : { next: pageParam };

    return client<LabelsResponseType>({
      url: END_POINTS.LABELS.GET_LABELS,
      params: {
        ...pagination,
        ...(searchTerm !== '' && { query: searchTerm }),
      },
    });
  };

  const labelApiQueryKey = useMemo(() => {
    return [QUERY_KEYS.LABELS.LABEL_LIST, searchTerm].filter(Boolean);
  }, [searchTerm]);

  const query = useInfiniteQuery(labelApiQueryKey, fetchLabels, {
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    getNextPageParam: lastPage =>
      lastPage?.cursor?.hasNext ? lastPage?.cursor?.next : undefined,
  });

  const { data, isLoading, hasNextPage, fetchNextPage } = query;

  // Transform paginated label data into a flat array and then into a map for efficient lookup
  // Combines all pages of label items into a single array, then creates a map keyed by labelId
  const labelList = useMemo(() => {
    let items: LabelItem[] = [];

    if (!data) {
      items = [];
    }

    if (data) {
      items = data.pages.reduce((acc: LabelItem[], cur) => {
        if (!isEmpty(cur?.items) && cur?.items) {
          acc.push(...cur.items);
        }

        return acc;
      }, []);
    }

    const labelMap = items.reduce((acc, item) => {
      acc[item.id] = item;

      return acc;
    }, {} as LabelList);

    return labelMap;
  }, [data]);

  return {
    isLabelListLoading: isLoading,
    labelList,
    hasNextLabelPage: hasNextPage,
    fetchNextLabelPage: fetchNextPage,
    fetchLabels,
  };
};
